/**
 * utils/needsTransformer.ts
 *
 * Transformation service that converts a list of needs into an optimized,
 * client-ready structure with:
 * - Sectioned grouping (identity, income, housing, etc.)
 * - Optimal ordering for client experience
 *
 * Updated Feb 2026: Removed conversational_intro (replaced by question-first paradigm).
 * Updated Feb 2026: Removed generateScreeningQuestions (conditions are now handled
 *   inline via stepSequenceBuilder).
 */

import type {
  NeedItem,
  GroupedNeeds,
  OrderedNeed,
} from '../types/needs';
import { SECTION_ORDER, SECTION_METADATA } from '../types/needs';

/**
 * Group needs by section for organized presentation
 */
export function groupNeedsBySection(needs: NeedItem[]): GroupedNeeds {
  const grouped: GroupedNeeds = {};

  needs.forEach((need) => {
    const section = need.section || 'informations';

    if (!grouped[section]) {
      grouped[section] = [];
    }

    grouped[section].push(need);
  });

  Object.keys(grouped).forEach((section) => {
    grouped[section].sort((a, b) => a.position - b.position);
  });

  return grouped;
}

/**
 * Order grouped needs optimally for client experience
 * Applies section ordering and generates presentation metadata
 */
export function orderNeedsForClient(groupedNeeds: GroupedNeeds): OrderedNeed[] {
  const orderedNeeds: OrderedNeed[] = [];
  let finalPosition = 0;

  const sections = Object.keys(groupedNeeds).sort((a, b) => {
    const orderA = SECTION_ORDER[a] ?? 999;
    const orderB = SECTION_ORDER[b] ?? 999;
    return orderA - orderB;
  });

  sections.forEach((section) => {
    const sectionNeeds = groupedNeeds[section];
    const sectionMeta = SECTION_METADATA[section] || SECTION_METADATA.informations;

    sectionNeeds.forEach((need, index) => {
      const isFirstInSection = index === 0;

      orderedNeeds.push({
        ...need,
        final_position: finalPosition,
        is_first_in_section: isFirstInSection,
        section_label: sectionMeta.label,
        section_icon: sectionMeta.icon,
      });

      finalPosition++;
    });
  });

  return orderedNeeds;
}
